import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box } from '@opendoor/bricks-next';
import type { GetMarketplacePropertyQuery, PropertyDetailsFragment } from '__generated__/athena';
import { withNavHeader } from 'cms/withNavHeader';
import { getAthenaClient } from 'components/api';
import {
  buildGalleryPhotos,
  formatStreetAddress,
  isBuyDirectEligible,
  usePropertyReactions,
  useSellerDisclosures,
  useSimilarProperties,
} from 'components/marketplace/helpers';
import {
  PropertyAnalytics,
  useTrackProperty,
} from 'components/marketplace/helpers/useTrackProperty';
import { GalleryPhotos, PhotoCarousel } from 'components/marketplace/property';
import type { PropertyDetailsType } from 'components/marketplace/property';
import MarketplaceLayout from 'components/properties/shared/MarketplaceLayout';
import type {
  CacheSafeGetServerSideProps,
  InferGetServerSidePropsType,
} from 'declarations/shared/serverSideProps';
import { ClientError } from 'graphql-request';
import dynamic from 'next/dynamic';

import { useObservability } from '../../../../helpers/observability';

const Lightbox = dynamic(
  () => import('components/marketplace/property/Lightbox').then((mod) => mod.Lightbox),
  {
    ssr: false,
  },
);

const ShareModal = dynamic(
  () => import('components/marketplace/property/ShareModal').then((mod) => mod.ShareModal),
  {
    ssr: false,
  },
);

const PropertySection = dynamic(
  () =>
    import('components/marketplace/property/PropertySection').then((mod) => mod.PropertySection),
  { ssr: false },
);

const Page: React.FC<InferGetServerSidePropsType<typeof getServerSideProps>> = (props) => {
  const property = props?.property;
  return (
    <PropertyAnalytics.Provider value={{ property }}>
      <PageInner {...props} />
    </PropertyAnalytics.Provider>
  );
};

const PageInner: React.FC<InferGetServerSidePropsType<typeof getServerSideProps>> = ({
  property,
  navHeader,
}) => {
  const { trackProperty } = useTrackProperty();
  const { trackJourneyEvent } = useObservability();
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(0);
  const toggleLightbox = useCallback(() => setIsLightboxOpen((prev) => !prev), []);
  // bricks-next `Lightbox` registers a document-level keydown listener that
  // fires `handleClose()` on Escape regardless of `isOpen`, so `handleClose`
  // must be close-only rather than a toggle.
  const closeLightbox = useCallback(() => setIsLightboxOpen(false), []);
  const handleSetSelectedPhotoIndex = useCallback((idx: number) => setSelectedPhotoIndex(idx), []);
  const { isLiked, onClickReaction } = usePropertyReactions(
    property.addressToken,
    'REACTION_SOURCE_WEB_PDP',
  );
  /**
   * The initial page load for customers will include the cached similar properties.
   * This hook executes client-side to retrieve the latest similar listings.
   */
  const marketplaceProperty: PropertyDetailsType = {
    ...property,
    isLiked,
    onClickReaction,
  };

  const { similarProperties } = useSimilarProperties(property.addressToken);
  if (similarProperties?.results) {
    marketplaceProperty.similarProperties = similarProperties;
  }

  const { sellerDisclosures } = useSellerDisclosures(property.addressToken);
  if (sellerDisclosures) {
    marketplaceProperty.sellerDisclosuresDownloadUrl = sellerDisclosures;
  }

  const formattedAddress = useMemo(
    () => formatStreetAddress(property.street, property.unitType, property.unitNumber),
    [property.street, property.unitType, property.unitNumber],
  );

  const carouselPhotos = useMemo(
    () => buildGalleryPhotos(marketplaceProperty),
    [marketplaceProperty.photos, marketplaceProperty.floorPlanPhotos],
  );

  useEffect(() => {
    trackProperty('property-view', 'marketplace-pdp');
    trackJourneyEvent('marketplaceProperty', 'detailPageView', {
      addressUuid: property.addressToken,
    });
  }, []);

  return (
    <MarketplaceLayout property={marketplaceProperty} navHeader={navHeader}>
      <Box
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        $smallerThanMD={{ flexDirection: 'column', overflowY: 'scroll' }}
        $largerThanSM={{ flexDirection: 'row', height: 'calc(100vh - 72px)' }}
        overflow="hidden"
        width="100vw"
      >
        <GalleryPhotos
          handleSetSelectedPhotoIndex={handleSetSelectedPhotoIndex}
          property={marketplaceProperty}
          toggleLightbox={toggleLightbox}
        />
        <PhotoCarousel
          photos={carouselPhotos}
          price={
            marketplaceProperty?.displayBadge === 'ON_THE_MARKET' &&
            isBuyDirectEligible(marketplaceProperty)
              ? marketplaceProperty.buyDirectPrice
              : marketplaceProperty.listPrice
          }
          address={formattedAddress}
          toggleLightbox={toggleLightbox}
          setSelectedPhotoIndex={handleSetSelectedPhotoIndex}
          latitude={marketplaceProperty.latitude}
          longitude={marketplaceProperty.longitude}
          openShareModal={() => setIsShareModalOpen(true)}
        />
        <Lightbox
          handleClose={closeLightbox}
          isOpen={isLightboxOpen}
          property={marketplaceProperty}
          selectedPhotoIndex={selectedPhotoIndex}
        />
        <PropertySection property={marketplaceProperty} />
        <ShareModal
          property={marketplaceProperty}
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
        />
      </Box>
    </MarketplaceLayout>
  );
};

const getServerSidePropsInner: CacheSafeGetServerSideProps<{
  property: PropertyDetailsFragment;
}> = async (context) => {
  const addressToken = context.query['address_id'];
  if (!addressToken || Array.isArray(addressToken)) {
    return {
      notFound: true,
    };
  }
  let propertyResult: GetMarketplacePropertyQuery | undefined;
  try {
    propertyResult = await getAthenaClient(context.req.headers).GetMarketplaceProperty({
      addressToken,
    });
  } catch (e) {
    if (e instanceof ClientError) {
      if (
        !(e.response.data as Partial<GetMarketplacePropertyQuery> | null)?.marketplace?.property
      ) {
        return {
          notFound: true,
        };
      }
      // send to sentry
    } else {
      return {
        notFound: true,
      };
    }
  }

  const property = propertyResult?.marketplace?.property;

  if (!property) {
    return {
      notFound: true,
    };
  }

  // If the property is in an unknown state, we should not show it.
  // Applies to "takedown" requests and other edge cases
  if (property.listingState === 'UNKNOWN') {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      property,
    },
  };
};

export const getServerSideProps = withNavHeader(getServerSidePropsInner);

export default Page;
